// Knowledge Base Icon Types (using lucide-react icon names)
// Re-export from schema to ensure consistency
export type KnowledgeBaseIcon = import('../schemas/knowledgeBase').KnowledgeBaseIcon;

// All available icons for knowledge base selection
export const KNOWLEDGE_BASE_ICONS: KnowledgeBaseIcon[] = [
  'Folder',
  'Crown',
  'Expand',
  'Triangle',
  'FlaskConical',
  'LayoutGrid',
  'Flag',
  'Diamond',
  'Glasses',
  'Box',
  'Clock',
  'Grid',
  'Sparkles',
  'Star',
];

// Knowledge Base Types
export interface KnowledgeBaseOwner {
  id: number | null;
  email: string | null;
  full_name: string | null;
}

export interface EmbeddingModel {
  id: number | null;
  name: string | null;
  display_name: string | null;
}

export interface KnowledgeBase {
  id: string; // UUID
  name: string;
  description: string | null;
  owner_id: number;
  owner: KnowledgeBaseOwner;
  is_public: boolean;
  is_active: boolean;
  is_default: boolean;
  embedding_model_id: number | null;
  embedding_model: EmbeddingModel;
  chunk_size: number;
  chunk_overlap: number;
  document_count: number;
  total_tokens: number;
  meta_data: Record<string, any> | null; // Will contain 'icon' field
  created_at: string;
  updated_at: string | null;
}

export interface KnowledgeBaseMetaData {
  icon?: KnowledgeBaseIcon;
  cover_url?: string; // Cover image API path (e.g., "/api/v1/knowledge-bases/covers/1_xxx.jpeg")
  logo_url?: string; // Deprecated: legacy logo URL for backward compatibility
  [key: string]: any;
}

// Discriminated union for exclusive icon/cover selection
export type KnowledgeBaseIconType =
  | { type: 'icon'; icon: KnowledgeBaseIcon }
  | { type: 'cover'; cover_url: string };

// Helper functions for easy construction
export const createIconData = (icon: KnowledgeBaseIcon): KnowledgeBaseIconType => ({
  type: 'icon',
  icon,
});

export const createCoverData = (coverUrl: string): KnowledgeBaseIconType => ({
  type: 'cover',
  cover_url: coverUrl,
});

// Helper to convert iconType to meta_data format for API
export const iconTypeToMetaData = (iconType: KnowledgeBaseIconType): KnowledgeBaseMetaData => {
  if (iconType.type === 'icon') {
    return { icon: iconType.icon };
  } else {
    return { cover_url: iconType.cover_url };
  }
};

// Create Knowledge Base Request
export interface CreateKnowledgeBaseRequest {
  name: string;
  description?: string | null;
  is_public?: boolean;
  embedding_model_id?: number | null;
  chunk_size?: number;
  chunk_overlap?: number;
  iconType?: KnowledgeBaseIconType; // Replaces meta_data for icon/logo selection
}

// Update Knowledge Base Request
export interface UpdateKnowledgeBaseRequest {
  name?: string | null;
  description?: string | null;
  is_public?: boolean | null;
  embedding_model_id?: number | null;
  chunk_size?: number | null;
  chunk_overlap?: number | null;
  meta_data?: KnowledgeBaseMetaData | null;
}

// List Response
export interface KnowledgeBaseListResponse {
  items: KnowledgeBase[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// List Query Parameters
export interface KnowledgeBaseListParams {
  skip?: number;
  limit?: number;
  search?: string;
  is_active?: boolean;
  is_public?: boolean;
  owner_id?: number;
  include_default?: boolean;
}

// File Status Types
export type KnowledgeBaseFileStatus =
  | 'pending'
  | 'uploading'
  | 'processing'
  | 'completed'
  | 'error';

// Knowledge Base File (simplified, will be updated based on actual API response)
export interface KnowledgeBaseFile {
  id: string;
  [key: string]: any;
}

// File List Response
export interface KnowledgeBaseFileListResponse {
  items: KnowledgeBaseFile[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

// File List Query Parameters
export interface KnowledgeBaseFileListParams {
  page?: number;
  page_size?: number;
  status?: KnowledgeBaseFileStatus;
}

// Share Permission Types
export type SharePermission = 'read' | 'edit';

// User Search Response
export interface UserSearchResponse {
  items: UserSearchUser[];
  total: number;
}

export interface UserSearchUser {
  id: number;
  email: string;
  full_name: string;
}

// Share Create Request
export interface KnowledgeBaseShareCreate {
  shared_with_user_id: number;
  permission: SharePermission;
}

// Share Update Request
export interface KnowledgeBaseShareUpdate {
  shared_with_user_id: number;
  permission: SharePermission;
}

// Share Response
export interface SharesResponse {
  items: KnowledgeBaseShare[];
}

// Knowledge Base Share
export interface KnowledgeBaseShare {
  id: number;
  knowledge_base_id: string;
  shared_with_user_id: number;
  shared_with_user: {
    id: number;
    email: string;
    full_name: string;
  };
  permission: SharePermission;
  created_at: string;
}
